import React from "react";
import logo from "../../../assets/svg/auth/logo.svg";
import useCustomScrollbar from "../../../styles/useCustomScrollbar";
import "../../../styles/customScrollbar.css";

interface TrialLayoutProps {
  children: React.ReactNode;
  /**
   * How wide the column inside may run.
   *
   * `wide` is the offer: a generous column that still stops short of the page,
   * so the picture and the price card have room without a 27-inch monitor
   * stretching two panels to the window's edge. `full` takes the page, which is
   * what a form wants: it is a screenful of fields, and the columns they sit in
   * read better long than deep.
   *
   * The extra room only arrives at 1400px, and the offer's larger type with it
   * — see `TrialPlanPage`. Below that the screen is the size it always was: a
   * laptop is where the column was already as wide as it should be, and giving
   * it more there is taken straight out of the margins.
   *
   * The confirmation uses neither, and no third value is kept for it: it runs
   * to the edge of the window on every side and draws its own shell.
   */
  width?: "wide" | "full";
  /**
   * Off for a screen that already carries the mark itself.
   *
   * The offer does: the artwork it opens with is supplied with the wordmark
   * drawn into it, and a second one in the corner above would be the same logo
   * twice on one screen. The other two steps have no artwork and keep it.
   */
  showLogo?: boolean;
}

/**
 * The shell the three trial screens share: the logo top-left, and the screen
 * itself centred under it.
 *
 * Not `AuthLayout`, though it is the same idea. That one is built around a
 * form column of a fixed, narrow width and pins legal copy to the foot; these
 * screens are two columns of cards and say their own small print. Not
 * `AppLayout` either, and that is the point of the flow: the sidebar appears
 * for the first time on the dashboard, once the trial has actually started.
 *
 * The screen scrolls inside itself rather than letting the document scroll,
 * which is what puts the app's own overlay scrollbar on it instead of the
 * platform's — the same thing `AuthLayout` does, for the same reason.
 */
const TrialLayout: React.FC<TrialLayoutProps> = ({
  children,
  width = "wide",
  showLogo = true,
}) => {
  // Reveals the thumb on hover, scroll and drag. Safe to call per screen: it
  // attaches once per element and cleans up on unmount.
  useCustomScrollbar();

  return (
    <div className="h-screen bg-white custom-scrollbar-overlay">
      {/* `min-h-full`, not `min-h-screen`: the height to fill is the scroll
          container's, or a screen taller than the viewport would be measured
          against the viewport twice and leave a dead band at the foot. */}
      <div className="min-h-full flex flex-col px-5 py-6 sm:px-12 sm:py-10 3xl:px-16 3xl:py-14">
        {showLogo && (
          <header>
            <img src={logo} alt="Naurist" className="h-6 3xl:h-7 w-auto" />
          </header>
        )}

        <main className="flex-1 flex items-center justify-center py-8 3xl:py-10">
          {/* Centred by the margin the shell already keeps on either side —
              `full` adds no width of its own, so the screen runs to the page's
              own padding and no further. */}
          <div
            className={`w-full ${
              width === "wide" ? "max-w-[1000px] min-[1400px]:max-w-[1360px] 3xl:max-w-[1500px]" : ""
            }`}
          >
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default TrialLayout;
